import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { hqApi, formatApiError } from "@/hq/hqApi";
import { Card, CardHeader, EmptyState, Skeleton } from "@/hq/HQPrimitives";

const STATE_LABEL = {
  DRAFT: { label: "Draft", tone: "muted" },
  READY: { label: "Ready", tone: "muted" },
  SCHEDULED: { label: "Scheduled", tone: "muted" },
  SENDING: { label: "Sending", tone: "info" },
  PARTIALLY_SENT: { label: "Partially sent — retryable remain", tone: "warn" },
  SENT: { label: "Sent", tone: "good" },
  DELIVERY_BLOCKED: { label: "Delivery blocked — email provider unavailable", tone: "bad" },
  PAUSED: { label: "Paused", tone: "muted" },
  CANCELLED: { label: "Cancelled", tone: "muted" },
  FAILED: { label: "Failed", tone: "bad" },
};
const STATE_TONE = {
  good: "bg-[#EBFBF2] text-[#0FA958] border-[#B7EBCF]",
  warn: "bg-[#FFF7EE] text-[#8A6D00] border-[#FCE1C7]",
  bad:  "bg-[#FDECE7] text-[#C1350B] border-[#F6C0AF]",
  info: "bg-[#F0F7FF] text-[#2E7CF6] border-[#D6E7FF]",
  muted:"bg-[#F2F2F2] text-[#4A4A4A] border-[#EAEAEA]",
};

export default function HQSalesCampaignDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [prospects, setProspects] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [validation, setValidation] = useState(null);
  const [sending, setSending] = useState(false);
  const [testEmail, setTestEmail] = useState("");

  async function load() {
    setLoading(true);
    try {
      const { data: c } = await hqApi.get(`/sales/campaigns/${id}`);
      setData(c);
      const { data: p } = await hqApi.get("/sales/prospects", { params: { include_demo: true, page_size: 100 } });
      setProspects(p.items || []);
      const { data: v } = await hqApi.get(`/sales/campaigns/${id}/validation`);
      setValidation(v);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  async function addSelected() {
    const ids = Array.from(selected);
    if (!ids.length) { toast("Select at least one prospect."); return; }
    try {
      const { data: r } = await hqApi.post(`/sales/campaigns/${id}/recipients`, { prospect_ids: ids });
      toast.success(`Added ${r.added}, skipped ${r.skipped_existing} existing.`);
      setSelected(new Set());
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  }
  async function sendTest() {
    if (!testEmail) { toast("Enter a test email address."); return; }
    try {
      const { data: r } = await hqApi.post(`/sales/campaigns/${id}/test`, { to_email: testEmail });
      toast.success(`Test send: ${r.status}${r.status === "SKIPPED" ? " (Resend not configured in preview)" : ""}`);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  }
  async function sendCampaign() {
    if (!window.confirm("Send this campaign now to all eligible recipients?")) return;
    setSending(true);
    try {
      const { data: r } = await hqApi.post(`/sales/campaigns/${id}/send`, { limit: 50 });
      toast.success(`Batch: sent ${r.sent}, skipped ${r.skipped}, failed ${r.failed}. State: ${r.campaign_state}`);
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(formatApiError(detail));
    } finally { setSending(false); }
  }

  async function retryAll() {
    if (!window.confirm("Retry all failed / provider-unavailable recipients?")) return;
    try {
      const { data: r } = await hqApi.post(`/sales/campaigns/${id}/retry`, { all_retryable: true });
      toast.success(`Retried ${r.retried}, refused ${r.refused}. State: ${r.campaign_state}`);
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  }

  if (loading) return <Skeleton h={200} />;
  if (!data) return <EmptyState title="Not found" testId="campaign-detail-empty" />;
  const c = data.campaign;
  const stateMeta = STATE_LABEL[c.state] || STATE_LABEL.DRAFT;
  const stateClass = STATE_TONE[stateMeta.tone] || STATE_TONE.muted;
  const canRetry = c.state === "DELIVERY_BLOCKED" || c.state === "PARTIALLY_SENT" || c.state === "FAILED";

  return (
    <div className="space-y-5" data-testid="campaign-detail-root">
      <Card>
        <div className="px-6 pt-6 pb-4">
          <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Campaign · {c.campaign_type}</div>
          <h1 className="mt-1 font-black text-2xl">{c.name}</h1>
          <div className="mt-2 flex gap-2 items-center text-[12px] flex-wrap">
            <span className={`px-2 py-1 rounded-full border text-[10px] font-black tracking-[0.16em] uppercase ${stateClass}`} data-testid={`campaign-state-${c.state}`}>
              {stateMeta.label}
            </span>
            <span className="text-[#8A8A8A]">Recipients: {c.recipient_count || 0} · Sent: {c.sent_count || 0} · Opens detected: {c.opens || 0} · Clicks: {c.clicks || 0} · Unsub: {c.unsubscribes || 0}</span>
          </div>
          <p className="mt-2 text-[11px] text-[#8A8A8A]">Email opens are detected via a tracking pixel. Mail-client privacy features, security scanners and image prefetching can trigger opens — treat them as a soft signal. Clicks, report views and enquiries are stronger.</p>
          {canRetry && (
            <div className="mt-3">
              <button onClick={retryAll} data-testid="campaign-retry-button" className="px-3 py-2 rounded-full bg-[#0A0A0A] text-white text-[11px] font-black tracking-[0.16em] uppercase">Retry retryable recipients</button>
            </div>
          )}
        </div>
      </Card>

      <Card>
        <CardHeader eyebrow="Pre-send validation" title={validation?.ok ? "Ready to send" : "Blocked"} />
        <div className="px-6 pb-6 pt-2 text-[13px]">
          {validation ? (
            <div data-testid="campaign-validation-block" className="space-y-2">
              <div>Eligible: <strong>{validation.eligible}</strong> of {validation.recipient_total}</div>
              {validation.reasons?.length ? (
                <ul className="list-disc pl-5 text-[#C1350B]">
                  {validation.reasons.map((r) => <li key={r} data-testid={`validation-reason-${r}`}>{r}</li>)}
                </ul>
              ) : (
                <div className="text-[#0FA958]">All checks passed.</div>
              )}
              {validation.warnings?.length ? (
                <ul className="list-disc pl-5 text-[#8A6D00]">
                  {validation.warnings.map((w) => <li key={w}>{w}</li>)}
                </ul>
              ) : null}
              <p className="text-[11px] text-[#8A8A8A]">Ineligible breakdown: no-email {validation.ineligible_no_email}, suppressed {validation.ineligible_suppressed}, DNC {validation.ineligible_dnc}.</p>
            </div>
          ) : "…"}
        </div>
      </Card>

      <Card>
        <CardHeader eyebrow="Test" title="Send test email" />
        <div className="px-6 pb-6 pt-2 flex flex-col sm:flex-row gap-2">
          <input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="you@example.com" data-testid="campaign-test-email" className="flex-1 px-3 py-2 border border-[#EAEAEA] rounded-full text-[13px]" />
          <button onClick={sendTest} data-testid="campaign-test-send" className="px-4 py-2 rounded-full border border-[#EAEAEA] text-[12px] font-bold tracking-[0.12em] uppercase">Send test</button>
        </div>
        <p className="px-6 pb-6 text-[11px] text-[#8A8A8A]">Test sends never mutate prospect stage or analytics.</p>
      </Card>

      <Card>
        <CardHeader eyebrow="Add recipients" title="From prospects" action={
          <button onClick={addSelected} data-testid="campaign-add-selected" className="px-3 py-2 rounded-full bg-[#0A0A0A] text-white text-[11px] font-black tracking-[0.16em] uppercase">Add selected ({selected.size})</button>
        } />
        <div className="px-6 pb-6 pt-2 max-h-[360px] overflow-y-auto">
          {!prospects.length ? (
            <EmptyState title="No prospects" testId="campaign-recipient-source-empty" />
          ) : (
            <table className="w-full text-[13px]">
              <tbody>
                {prospects.map((p) => {
                  const ineligible = p.opt_out || p.suppressed || p.do_not_contact || !p.normalized_email;
                  return (
                    <tr key={p.id} className="border-t border-[#EAEAEA]">
                      <td className="py-2 w-6">
                        <input
                          type="checkbox"
                          disabled={ineligible}
                          checked={selected.has(p.id)}
                          onChange={(e) => {
                            const s = new Set(selected);
                            e.target.checked ? s.add(p.id) : s.delete(p.id);
                            setSelected(s);
                          }}
                          data-testid={`campaign-recipient-check-${p.id}`}
                        />
                      </td>
                      <td className="py-2">{p.business_name} <span className="text-[11px] text-[#8A8A8A]">{p.normalized_domain || p.normalized_email}</span></td>
                      <td className="py-2 text-[11px] text-[#8A8A8A]">{ineligible ? "ineligible" : p.stage}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </Card>

      <Card>
        <CardHeader eyebrow="Recipients" title={`In this campaign (${data.recipients?.length || 0})`} />
        <div className="px-6 pb-6 pt-2 max-h-[360px] overflow-y-auto">
          {!data.recipients?.length ? (
            <EmptyState title="No recipients yet" testId="campaign-recipients-empty" />
          ) : (
            <table className="w-full text-[13px]">
              <thead><tr className="text-left text-[10px] tracking-[0.24em] uppercase text-[#8A8A8A]">
                <th className="py-2">Email</th><th>Status</th><th>Opens</th><th>Clicks</th>
              </tr></thead>
              <tbody>
                {data.recipients.map((r) => (
                  <tr key={r.id} className="border-t border-[#EAEAEA]" data-testid={`campaign-recipient-row-${r.id}`}>
                    <td className="py-2">{r.email_normalized || "—"}</td>
                    <td className="py-2 text-[12px]">{r.send_status}{r.skip_reason ? ` (${r.skip_reason})` : ""}</td>
                    <td className="py-2" title="Open detected — noisy signal">{r.opens || 0}</td>
                    <td className="py-2">{r.clicks || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Card>

      <Card>
        <div className="px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-[12px] text-[#8A8A8A]">Sending uses claim-first → send-second — no duplicate emails to a recipient.</div>
          <button
            onClick={sendCampaign}
            disabled={sending || !validation?.ok}
            data-testid="campaign-send-button"
            className="px-6 py-3 rounded-full bg-[#FF6A00] text-black text-[13px] font-black tracking-[0.16em] uppercase disabled:opacity-50"
          >{sending ? "Sending…" : "Send campaign"}</button>
        </div>
      </Card>
    </div>
  );
}
